/* eslint-disable func-names */
/* eslint-disable prefer-arrow-callback */
import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import toast from 'common/utils/toast'
import Modal from 'components/Modal'
import ChangePasswordForm from './ChangePasswordForm'
import { Formik } from 'formik'
import * as yup from 'yup'
import sha512 from 'crypto-js/sha512'
import md5 from 'crypto-js/md5'
import userApi from 'resources/user'
import { messageServiceError } from 'help/message'
import { parseJwt } from 'help/jwt'

export const getPasswordChecks = (values, desLogin) => {
  const newPassword = (values && values.newPassword) || ''
  const newPasswordConfirmation = (values && values.newPasswordConfirmation) || ''
  const hasContent = newPassword.length > 0
  const hasConfirm = newPasswordConfirmation.length > 0
  const loginLower = (desLogin || '').toLowerCase()
  return [
    ...getCompositionChecks(values, desLogin),
    { id: 'match', label: 'general.passwordRule.match', ok: hasConfirm && newPassword === newPasswordConfirmation }
  ]
}

/** Critérios RF002 (composição) — sem igualdade com a confirmação. */
export const getCompositionChecks = (values, desLogin) => {
  const newPassword = (values && values.newPassword) || ''
  const hasContent = newPassword.length > 0
  const loginLower = (desLogin || '').toLowerCase()
  return [
    { id: 'min15', label: 'general.passwordRule.min15', ok: newPassword.length >= 15 },
    { id: 'uppercase', label: 'general.passwordRule.uppercase', ok: /[A-Z]/.test(newPassword) },
    { id: 'lowercase', label: 'general.passwordRule.lowercase', ok: /[a-z]/.test(newPassword) },
    { id: 'digit', label: 'general.passwordRule.digit', ok: /\d/.test(newPassword) },
    { id: 'special', label: 'general.passwordRule.special', ok: /[#?!@$%^&*\-._]/.test(newPassword) },
    { id: 'noSpace', label: 'general.passwordRule.noSpace', ok: hasContent && !/\s/.test(newPassword) },
    { id: 'noUsername', label: 'general.passwordRule.noUsername', ok: hasContent && (!loginLower || !newPassword.toLowerCase().includes(loginLower)) }
  ]
}

export const buildChangePasswordValidationSchema = (desLogin, { requireOldPassword = true } = {}) => {
  const shape = {
    newPassword: yup
      .string()
      .required('general.fieldRequired')
      .test('composition', 'general.passwordRule.allChecksRequired', function (value) {
        return getCompositionChecks({ ...this.parent, newPassword: value }, desLogin).every(c => c.ok)
      }),
    newPasswordConfirmation: yup
      .string()
      .required('general.fieldRequired')
      .test('match', 'general.passwordsDidntMatch', function (newPasswordConfirmation) {
        const { newPassword } = this.parent
        if (!newPasswordConfirmation) {
          return true
        }
        return newPasswordConfirmation === newPassword
      })
  }

  if (requireOldPassword) {
    shape.oldPassword = yup.string().required('general.fieldRequired')
  }

  return yup.object().shape(shape)
}

const initialValues = {
  oldPassword: '',
  newPassword: '',
  newPasswordConfirmation: ''
}

function ChangePasswordModal ({ modalState, setModalState, logout }) {
  const { t } = useTranslation()
  const { openModal } = modalState

  const [submittingData, setSubmittingData] = useState(false)

  const desLogin = useMemo(() => {
    const token = window.localStorage.getItem('access_token')
    return parseJwt(token).user_name || ''
  }, [])

  const validationSchema = useMemo(() => buildChangePasswordValidationSchema(desLogin), [desLogin])

  const handleToggleModal = useCallback((opened) => {
    setModalState(prevState => ({
      ...prevState,
      openModal: opened
    }))
  }, [setModalState])

  const sendData = useCallback((values, { setSubmitting }) => {
    const getEncryptedPassword = password => sha512(md5(password).toString().toUpperCase()).toString().toUpperCase()
    const { newPassword, oldPassword } = values
    setSubmittingData(true)
    userApi.changePassword({
      desSenha: getEncryptedPassword(newPassword),
      desSenhaAntiga: getEncryptedPassword(oldPassword)
    })
      .then(() => {
        toast.success(t('general.passwordSuccessfullyChanged'))
        handleToggleModal(false)
        logout()
      })
      .catch(messageServiceError)
      .finally(() => {
        setSubmittingData(false)
        setSubmitting(false)
      })
  }, [handleToggleModal, logout, t])

  return openModal ? (
    <Modal
      showModal={openModal}
      toggleModal={!submittingData ? handleToggleModal : () => {}}
      disableBackdropClick={submittingData}
      modalTitle={t('general.changePassword')}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={sendData}>
        {props => {
          const checks = getPasswordChecks(props.values, desLogin)
          return <ChangePasswordForm {...props} handleToggleModal={handleToggleModal} checks={checks} />
        }}
      </Formik>
    </Modal>) : null
}

ChangePasswordModal.propTypes = {
  modalState: PropTypes.shape({
    openModal: PropTypes.bool,
    data: PropTypes.object
  }).isRequired,
  setModalState: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired
}

export default ChangePasswordModal
